import type { AwardCode, PublicView } from '@ringmaster/shared';

/**
 * The sitting log — what a guild has actually played together in this room,
 * this visit, remembered from PUBLIC END views only.
 *
 * The server has no concept of a "sitting": a room's games are just a sequence
 * of PLAY_AGAIN cycles, and the cumulative `PlayerPublic.score` is the only
 * thing that carries across them. But the two things the END screen wants to
 * say — "your third game tonight" and "here is what happened in all three" —
 * need the games themselves, so the store keeps a compact record of each one as
 * it watches the room reach END (net/useRoom.ts).
 *
 * TWO RULES, both load-bearing:
 *
 *  1. PUBLIC DATA ONLY. Everything here comes off `PublicView` at END, where
 *     the truth is already public (`result.roles`, `oneNight.reveal`, the
 *     server-patched `awards`). No PrivateView reaches this module, so no
 *     secret can enter the log — the e2e leak sweep's guarantee is unchanged
 *     by construction, not by a test.
 *  2. THIS VISIT, THIS ROOM. The log lives in the zustand store, so it starts
 *     empty on a reload and is cleared when the client joins a different room.
 *     It therefore UNDERCOUNTS a host who refreshed mid-sitting and never
 *     overcounts — the right direction for a gate that unlocks a share strip.
 */

/** One completed game, as the sitting remembers it. */
export interface SittingGame {
  /** Which game was played (a room may switch games between rematches). */
  gameId: string;
  winner: 'VILLAGE' | 'WOLVES';
  /** Blood Moon only: the wolves closed it out before the day ever broke. */
  atDawn: boolean;
  /**
   * The names the reveal exposed as the hidden side — the wolves, or The
   * Whisper's Deceiver. Empty when the hidden side was never in play (One
   * Night can strand both wolf cards in the centre) or on a 2p Whisper table
   * that deals no Deceiver at all.
   */
  traitors: string[];
  /** Which word the copy should use for `traitors`. */
  traitorKind: 'wolves' | 'deceiver';
  /** The game's biggest bonus award (never WIN, which everyone on the side banks). */
  topAward: { name: string; code: AwardCode } | null;
  /**
   * When the store logged this game, epoch ms — stamped at the append, not read
   * off the view (no END view carries a finish time). Optional because the log
   * is a plain record and a caller may build one without it; every entry the
   * store makes has it. Read by the END screen to tell "this guild has played
   * inside the live week" from "this guild played, but that was last week"
   * (ui/guildStreak.ts).
   */
  endedAt?: number;
}

/** How many games the log keeps — a sitting far longer than any observed one. */
export const SITTING_LOG_MAX = 24;

/**
 * Distil one END public view into a log entry, or null when the view is not a
 * finished game (no phase check here — the caller owns the END transition).
 */
export function sittingGameFrom(view: PublicView): SittingGame | null {
  const result = view.result;
  if (!result) return null;

  const nameOf = new Map(view.players.map((p) => [p.id, p.name]));
  const isWhisper = view.gameId === 'whisper';
  // One Night is decided by the card HELD at dawn, not the one dealt — the same
  // rule ui/endVerdict.ts applies to a player's own verdict, applied here to the
  // table's. Every other game's `result.roles` is already the final truth.
  const finalRoles = view.oneNight?.reveal?.finalRoles ?? result.roles;
  const hidden = isWhisper ? 'DECEIVER' : 'WEREWOLF';

  return {
    gameId: view.gameId ?? 'bloodmoon',
    winner: result.winner,
    atDawn: result.atDawn === true,
    traitors: Object.entries(finalRoles)
      .filter(([, role]) => role === hidden)
      .map(([id]) => nameOf.get(id) ?? id),
    traitorKind: isWhisper ? 'deceiver' : 'wolves',
    topAward: topAwardOf(view, nameOf),
  };
}

/**
 * The game's standout bonus: the highest-scoring award that is not WIN. WIN is
 * excluded because every player on the winning side banks it, so it names
 * nobody in particular. Ties keep the first row, which is the engine's own
 * order — deterministic, and good enough for a line of flavour text.
 */
function topAwardOf(view: PublicView, nameOf: Map<string, string>): SittingGame['topAward'] {
  let best: { name: string; code: AwardCode; points: number } | null = null;
  for (const a of view.awards ?? []) {
    if (a.code === 'WIN') continue;
    if (best && a.points <= best.points) continue;
    best = { name: nameOf.get(a.playerId) ?? a.playerId, code: a.code, points: a.points };
  }
  return best ? { name: best.name, code: best.code } : null;
}
